import { Map, List } from 'immutable'
import { createSelector } from 'reselect'
import {
  getIn,
  getInOr,
  pick,
  pickToArray,
  filter,
  get,
  getEq,
  map,
  valueSeq,
  reduce,
  sortBy,
  findKeysBy,
  getSatisfies,
  flipGet,
} from 'utils/immutable'
import {
  COMPLEX,
  TIMER,
  ALL_RUNG_NAMES,
  SIMPLE,
  BOOLEAN,
  DIGITAL_INPUT,
  NATIVE_INPUT,
  RUNG_ENABLE,
  RUNG_RESET,
} from 'utils/constants'
import { conditionLabel } from 'i18n/displayConstants/conditions'
import { createToJsSelector } from 'selectors/utils'
import { either, compose, equals } from 'ramda'
import { getDescriptionNextIndex } from 'selectors/feature'
import { getFeatureModuleName } from 'selectors/feature/featureMetadata'
import {
  getAllFuncGraphNodeUris,
  getOrphanQueryUris,
} from 'selectors/feature/functionGraphs'
import findTranslations from 'utils/findTranslations'
import {
  formatSignalDescription,
  determineDisabledInputOptions,
  LIMITED,
  RESTRICTED,
} from './utils'

const queryTypeEq = getEq('queryType')

const outputsSelector = getIn(['feature', 'currentFeature', 'outputs'])
const outputMapsSelector = getIn(['feature', 'currentFeature', 'outputMaps'])
const queriesSelector = getIn(['feature', 'currentFeature', 'queries'])
const conditionsSelector = getInOr(Map(), [
  'feature',
  'currentFeature',
  'conditions',
])
const signalsSelectorForModules = getIn(['feature', 'signals'])

const querySelector = (state, props) =>
  state.getIn(['feature', 'currentFeature', 'queries', props.queryUri])

const outputQueryUriSelector = (state, props) =>
  state.getIn([
    'feature',
    'currentFeature',
    'outputMaps',
    props.fnId,
    'outputQuery',
  ])

// resolve cirular dependency (copy/paste from signals file)
// TODO: move shared dependecies into separate file
const signalsSelector = createSelector(
  signalsSelectorForModules,
  getFeatureModuleName,
  (allSignals, moduleName) => {
    if (!allSignals || !moduleName) return Map()
    return allSignals.get(moduleName) || Map()
  }
)

const signalSelector = createSelector(
  signalsSelector,
  (_, props) => props.signal,
  (signals, signal) => {
    if (!signal) return Map()
    return signals.get(signal) || Map()
  }
)

const getSignalDescription = createSelector(signalSelector, (signal) =>
  signal ? signal.get('description') : null
)

const orphanDescriptionsSelector = createSelector(
  getOrphanQueryUris,
  queriesSelector,
  (orphansUris, queries) => {
    const initialMap = Map({
      [COMPLEX]: List(),
      [TIMER]: List(),
    })

    const orphans = orphansUris.map((uri) => queries.get(uri))

    return orphans.reduce(
      (acc, orphan) =>
        acc.update(orphan.get('queryType'), (type) =>
          type.concat(queries.getIn([orphan.get('uri'), 'description']))
        ),
      initialMap
    )
  }
)

const getOrphanDescriptions = createToJsSelector(orphanDescriptionsSelector)

const selectFunctionOutputById = createSelector(
  outputsSelector,
  (_, { id }) => id,
  flipGet
)

const getInputRestrictionFromOutput = createSelector(
  selectFunctionOutputById,
  determineDisabledInputOptions
)

const logicNodesSelector = createSelector(
  queriesSelector,
  filter(either(queryTypeEq(COMPLEX), queryTypeEq(TIMER)))
)

const getLogicNodes = createToJsSelector(logicNodesSelector)

const logicNodeRungUris = createSelector(
  querySelector,
  pickToArray(ALL_RUNG_NAMES)
)

// Logic Nodes that a Logic Node is carried from
const outputtedLogicSetsSelector = createSelector(
  logicNodesSelector,
  conditionsSelector,
  (_, { uri }) => uri,
  (logicNodes, conditions, uri) => {
    const getMappedConditions = reduce((acc, condition) => {
      if (getEq('refQueryUri', uri, condition)) {
        const conditionUri = condition.get('uri')
        const parentQueryUri = condition.get('parentQueryUri')
        const parentQuery = logicNodes.get(parentQueryUri)
        const parentQueryDescription = parentQuery.get('description')
        const parentQueryType = parentQuery.get('queryType')

        const conditionIndex = parentQuery
          .get('conditions')
          .findIndex(equals(conditionUri))

        if (parentQueryType === TIMER) {
          return acc.push(
            Map({
              description: conditionLabel(
                parentQueryDescription,
                conditionIndex === 0 ? RUNG_ENABLE : RUNG_RESET
              ),
              uri: conditionUri,
            })
          )
        }

        return acc.push(
          Map({
            description: conditionLabel(parentQueryDescription, conditionIndex),
            uri: conditionUri,
          })
        )
      }

      return acc
    }, List())

    return compose(sortBy(get('description')), getMappedConditions)(conditions)
  }
)

// Outputs that a Logic Node is carried from
const outputtedOutputsSelector = createSelector(
  outputMapsSelector,
  outputsSelector,
  (_, { uri }) => uri,
  (outputMaps, allOutputs, uri) => {
    // Check if logic node is an outputQuery, if so
    // get the functionIds that it's an outputQuery for
    const outputsFunctionIds = findKeysBy(getEq('outputQuery', uri), outputMaps)

    if (outputsFunctionIds.isEmpty()) return List()

    // if the logic node is an outputQuery
    // get all outputs for those functions
    return allOutputs.reduce((acc, output) => {
      if (
        getSatisfies((fnId) => outputsFunctionIds.has(fnId), 'fnId', output)
      ) {
        return acc.push(pick(['description', 'id'], output))
      }

      return acc
    }, List())
  }
)

// Merged List of carries from a Logic Set
const outputtingDataToList = createSelector(
  outputtedLogicSetsSelector,
  outputtedOutputsSelector,
  (outputtedLogicSets, outputtedOutputs) =>
    outputtedOutputs.concat(outputtedLogicSets)
)

const getOutputtingDataToList = createToJsSelector(outputtingDataToList)

// TODO: distinguish between condition and box types
// queryType - simple, complex, timer
// subType - analogInput, digitalInput, can
// this accounts for either. It's getting confusing
const logicNodeSelector = createSelector(
  querySelector,
  conditionsSelector,
  signalsSelector,
  (_, props) => props,
  (q, conditions, signals, { queryUri }) => {
    const query = q || conditions.get(queryUri)
    if (!query) return Map()

    const signalUri = query.get('signal')
    if (!signalUri) return query
    // below content should only be for conditions (currently subqueries)

    const signal = signals.get(signalUri)
    const inputConfig = query.get('inputConfig')

    if (!signal) return Map()

    return query
      .merge(
        pick(
          [
            'dataType',
            'defaultValue',
            'translationType',
            'connector',
            'location',
            'switchNumber',
          ],
          signal
        )
      )
      .merge(
        pick(
          ['units', 'minValue', 'maxValue', 'translations'],
          findTranslations(signal, inputConfig)
        )
      )
      .merge({
        pinDescription: formatSignalDescription(signal),
      })
  }
)

const outputQueryLogicNodeSelector = createSelector(
  outputQueryUriSelector,
  (state) => state,
  (queryUri, state) => {
    if (!queryUri) return Map()
    const logicNode = logicNodeSelector(state, { queryUri })
    const signalDescription = getSignalDescription(state, {
      signal: logicNode.get('signal'),
    })
    return logicNode.merge({ signalDescription })
  }
)

const makeOutputQueryLogicNode = () =>
  createToJsSelector(outputQueryLogicNodeSelector)

const makeGetNewLogicNodeIndex = (nodeType) =>
  createSelector(queriesSelector, (queries) =>
    getDescriptionNextIndex(queries, nodeType)
  )

const makeLogicNodeSelector = () => createToJsSelector(logicNodeSelector)
const getLogicNode = createToJsSelector(logicNodeSelector)

const logicNodeConditionsSelector = createSelector(
  logicNodeSelector,
  conditionsSelector,
  (logicNode, allConditions) => {
    const conditions = allConditions.filter(
      (condition) => condition.get('parentQueryUri') === logicNode.get('uri')
    )
    return conditions
  }
)

const getLogicNodeConditions = createToJsSelector(logicNodeConditionsSelector)

const availableLogicNodesMetaByFunction = createSelector(
  getAllFuncGraphNodeUris,
  queriesSelector,
  (functionQueries, allQueries) =>
    compose(
      map(pick(['uri', 'description', 'id', 'queryType'])),
      pickToArray(functionQueries)
    )(allQueries)
)

const getFilterList = (flag) => {
  if (flag === RESTRICTED) {
    return queryTypeEq(COMPLEX)
  }
  if (flag === LIMITED) {
    return queryTypeEq(TIMER)
  }
  return either(queryTypeEq(TIMER), queryTypeEq(COMPLEX))
}

const availableLogicNodesMetaSelector = createSelector(
  queriesSelector,
  getInputRestrictionFromOutput,
  (queries, inputRestriction) => {
    if (!queries) return List()
    const filterList = getFilterList(inputRestriction)
    return compose(
      (seq) =>
        seq.sort((a, b) =>
          a
            .get('description')
            .localeCompare(b.get('description'), undefined, { numeric: true })
        ),
      valueSeq,
      map(pick(['uri', 'description', 'id', 'queryType'])),
      filter(filterList)
    )(queries)
  }
)

const getAvailableLogicNodesMeta = createToJsSelector(
  availableLogicNodesMetaSelector
)

const getLogicNodesMetaByFunction = createToJsSelector(
  availableLogicNodesMetaByFunction
)

const getMustResetCanBitNumber = createSelector(
  outputQueryUriSelector,
  queriesSelector,
  signalsSelector,
  (outputQueryUri, queries, signals) => {
    if (!outputQueryUri || !queries) return false

    const outputQuery = queries.get(outputQueryUri)
    const outputQueryTypeEq = (prop) => queryTypeEq(prop, outputQuery)
    const subTypeEq = (prop) => getEq('subType', prop, outputQuery)

    const querySignal = outputQuery && outputQuery.get('signal')
    const signal = signals.get(querySignal)
    const isBooleanSignal = getEq('translationType', BOOLEAN, signal)

    const isComplex = outputQueryTypeEq(COMPLEX)
    const isTimer = outputQueryTypeEq(TIMER)
    const isSimple = outputQueryTypeEq(SIMPLE)

    const isSimpleDigitalInput = isSimple && subTypeEq(DIGITAL_INPUT)
    const isSimpleNativeBooleanInput =
      isSimple && subTypeEq(NATIVE_INPUT) && isBooleanSignal

    return (
      isComplex || isTimer || isSimpleDigitalInput || isSimpleNativeBooleanInput
    )
  }
)

export {
  availableLogicNodesMetaSelector,
  availableLogicNodesMetaByFunction,
  makeLogicNodeSelector,
  getLogicNode,
  makeGetNewLogicNodeIndex,
  getLogicNodesMetaByFunction,
  getAvailableLogicNodesMeta,
  conditionsSelector,
  logicNodeRungUris,
  getOutputtingDataToList,
  getInputRestrictionFromOutput,
  getMustResetCanBitNumber,
  getOrphanDescriptions,
  makeOutputQueryLogicNode,
  getLogicNodes,
  getLogicNodeConditions,
  // for tests
  outputtedOutputsSelector,
  outputtedLogicSetsSelector,
}
